import React, { useState, useEffect, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { getConversationByIdAdminAPI } from "../reduxData/user/userAction";

const LinkifyText = ({ children }) => {
  if (!children) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = children.split(urlRegex);

  return (
    <>
      {parts.map((part, index) =>
        part.match(urlRegex) ? (
          <a key={index} href={part} target="_blank" rel="noopener noreferrer" className="text-primary">
            {part}
          </a>
        ) : (
          part
        )
      )}
    </>
  );
};

const MessageContent = ({ msg }) => {
  if (msg.type === 'image' && msg.mediaUrl) {
    return (
      <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer">
        <img
          src={msg.mediaUrl}
          alt="User attachment"
          style={{ maxWidth: '200px', borderRadius: '8px', marginTop: '5px' }}
        />
      </a>
    );
  }

  if (msg.body) {
    return (
      <p className="mb-0 bg-light p-2 rounded">
        <LinkifyText>{msg.body}</LinkifyText>
      </p>
    );
  }

  return <p className="mb-0 bg-light p-2 rounded text-muted">[Unsupported message format]</p>;
};

const ViewConversationModal = ({ show, onHide, conversationId }) => {
  const [conversation, setConversation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchConversationDetails = async () => {
      if (!conversationId) return;
      setIsLoading(true);
      try {
        const data = await getConversationByIdAdminAPI(conversationId);
        setConversation(data);
      } finally {
        setIsLoading(false);
      }
    };

    if (show) {
      fetchConversationDetails();
    } else {
      setConversation(null);
    }
  }, [conversationId, show]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation?.messages]);

  const renderHeader = () => {
    if (!conversation) return "Conversation Details";
    const participants = conversation.participants
      .map((p) => p.user_name)
      .join(" & ");
    const itemName = conversation.relatedItem?.item_name;
    return (
      <div>
        <h5 className="mb-0">{participants}</h5>
        {itemName && (
          <small className="text-muted">Regarding: {itemName}</small>
        )}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{renderHeader()}</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ minHeight: "60vh", display: "flex", flexDirection: "column" }}
      >
        {isLoading && <p className="text-center">Loading conversation...</p>}
        {!isLoading && conversation && (
          <div
            className="flex-grow-1"
            style={{ overflowY: "auto", padding: "1rem" }}
          >
            {conversation.messages.length > 0 ? (
              conversation.messages.map((msg, index) => (
                <div key={msg._id || index} className="d-flex mb-3">
                  <img
                    src={msg.sender?.profile_pic || "https://placehold.co/50"}
                    alt={msg.sender?.user_name}
                    className="rounded-circle"
                    style={{
                      width: "40px",
                      height: "40px",
                      marginRight: "15px",
                    }}
                  />
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between">
                      <strong className="mb-1">
                        {msg.sender?.user_name || "User"}
                      </strong>
                      <small className="text-muted">
                        {new Date(msg.createdAt).toLocaleString()}
                      </small>
                    </div>
                     <MessageContent msg={msg} />
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-muted">
                No messages in this conversation yet.
              </p>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewConversationModal;
